import { zodResolver } from '@hookform/resolvers/zod';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { DialogTitle } from '@onesource/components';
import { useRequestEmailChangeMutation } from '@onesource/services';
import { AxiosError } from 'axios';
import { enqueueSnackbar } from 'notistack';
import { FC, useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { MdClose, MdMarkEmailRead } from 'react-icons/md';
import { TfiEmail } from 'react-icons/tfi';
import { z } from 'zod';

// ----------------------------------------------------------------------

const ChangeEmailFormSchema = z.object({
  newEmail: z.string().min(1, 'New email is required').email('Must be a valid email'),
  currentPassword: z.string().min(1, 'Current password is required'),
});
type TChangeEmailForm = z.infer<typeof ChangeEmailFormSchema>;

export type ChangeEmailDialogProps = {
  open: boolean;
  onClose: () => void;
  /** The user's current (still active) login email. */
  currentEmail: string;
};

export const ChangeEmailDialog: FC<ChangeEmailDialogProps> = ({ open, onClose, currentEmail }) => {
  const mutation = useRequestEmailChangeMutation();

  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm<TChangeEmailForm>({
    resolver: zodResolver(ChangeEmailFormSchema),
    defaultValues: { newEmail: '', currentPassword: '' },
    mode: 'all',
  });

  // Reset the form whenever the dialog is (re)opened.
  useEffect(() => {
    if (open) {
      reset({ newEmail: '', currentPassword: '' });
      mutation.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const newEmail = watch('newEmail');

  const serverError =
    mutation.error instanceof AxiosError && mutation.error.response?.data?.message
      ? (mutation.error.response.data.message as string)
      : mutation.isError
        ? 'Could not start the email change. Please try again.'
        : null;

  const handleClose = () => {
    if (mutation.isPending) {
      return;
    }
    onClose();
  };

  const onSubmit = handleSubmit(async data => {
    const origin = window.location.origin;
    try {
      await mutation.mutateAsync(
        {
          newEmail: data.newEmail,
          currentPassword: data.currentPassword,
          verifyRedirectUrl: `${origin}/auth/verify-email-change`,
          cancelRedirectUrl: `${origin}/auth/cancel-email-change`,
        },
        {
          onSuccess: result => {
            enqueueSnackbar(`Verification link sent to ${result.newEmail}`, {
              variant: 'success',
              anchorOrigin: { vertical: 'bottom', horizontal: 'center' },
            });
            onClose();
          },
        },
      );
    } catch {
      // Error surfaced inline via `serverError`.
    }
  });

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle
        onClose={handleClose}
        icon={<TfiEmail size={18} />}
        subtitle="Verify a new address before it becomes your login email"
      >
        Change primary email
      </DialogTitle>
      <form onSubmit={onSubmit} noValidate>
        <DialogContent dividers>
          <Stack spacing={2.5}>
            <Typography variant="body2" color="text.secondary">
              For your security, changing your login email requires your password and verification of the new address.
              Your current email stays active until you confirm the link we send.
            </Typography>

            <Box
              sx={{
                p: 2,
                borderRadius: 1,
                bgcolor: 'background.neutral',
                border: theme => `1px solid ${theme.palette.divider}`,
              }}
            >
              <Typography variant="caption" color="text.secondary">
                Current email
              </Typography>
              <Typography variant="body2" fontWeight={600} sx={{ wordBreak: 'break-all' }}>
                {currentEmail}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                New email
              </Typography>
              <Typography variant="body2" fontWeight={600} color="primary.main" sx={{ wordBreak: 'break-all' }}>
                {newEmail || '—'}
              </Typography>
            </Box>

            {serverError && <Alert severity="error">{serverError}</Alert>}

            <Controller
              name="newEmail"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="New email address"
                  type="email"
                  fullWidth
                  autoComplete="email"
                  disabled={mutation.isPending}
                  error={!!errors.newEmail}
                  helperText={errors.newEmail?.message}
                />
              )}
            />

            <Controller
              name="currentPassword"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Current password"
                  type="password"
                  fullWidth
                  autoComplete="current-password"
                  disabled={mutation.isPending}
                  error={!!errors.currentPassword}
                  helperText={errors.currentPassword?.message}
                />
              )}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleClose} variant="outlined" disabled={mutation.isPending} startIcon={<MdClose />}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={mutation.isPending}
            startIcon={mutation.isPending ? <CircularProgress size={16} color="inherit" /> : <MdMarkEmailRead />}
          >
            {mutation.isPending ? 'Sending…' : 'Send verification link'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};
